import { Icon } from '@iconify/react';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UrlInput } from './components/UrlInput';
import { AdvancedOptions } from '@/Domain/components/KnowledgeBase/UploadUrlModal/components/AdvancedOptions';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { FormControl, FormLabel } from '@/Domain/components/Shared/Form/Form';
import { FormItem, FormMessage } from '@/Domain/components/Shared/FormCustom/FormCustom';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Tabs, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import { WebsiteModalTypeEnum } from '@/Domain/enum/WebsiteModalType.enum';
import { UploadUrlModalHelper } from '@/Domain/helper/UploadUrlModal.helper';
import { UploadUrlForm, useUploadUrlForm } from '@/Domain/hooks/KnowledgeBase/useImportUrl.hook';
import { MODAL_NAMES } from '@/Domain/store/modal.store';

type Props = {
  type?: WebsiteModalTypeEnum;
  isPending?: boolean;
};

const URL_TABS = [
  {
    value: WebsiteModalTypeEnum.ALL_PAGES,
    labelKey: 'modals.upload_url.all_pages.title',
    testId: 'upload-url-tab-all-pages',
  },
  {
    value: WebsiteModalTypeEnum.SINGLE,
    labelKey: 'modals.upload_url.single_page.title',
    testId: 'upload-url-tab-single',
  },
  {
    value: WebsiteModalTypeEnum.MULTIPLE,
    labelKey: 'modals.upload_url.multiple_pages.title',
    testId: 'upload-url-tab-multiple',
  },
  {
    value: WebsiteModalTypeEnum.SITEMAP,
    labelKey: 'modals.upload_url.sitemap.title',
    testId: 'upload-url-tab-sitemap',
  },
] as const;

// Tabbed modal: single UPLOAD_URL_MODAL_ID, 4 tabs for URL import types
const TabbedUploadUrlModal: React.FC<{ externalIsPending: boolean }> = ({ externalIsPending }) => {
  const { t } = useTranslation(['knowledge_base']);
  const [activeTab, setActiveTab] = useState<WebsiteModalTypeEnum>(WebsiteModalTypeEnum.ALL_PAGES);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    form,
    handleSubmit,
    handleClose,
    handleUrlError,
    handleAdvancedOptionsChange,
    validateFormUrl,
    isUrlValid,
    isPending: hookIsPending,
    isSubmitting,
    isModalOpen,
    resetForm,
  } = useUploadUrlForm(activeTab, MODAL_NAMES.UPLOAD_URL_MODAL_ID);

  const isPending = externalIsPending || hookIsPending;

  useEffect(() => {
    if (!isModalOpen) {
      resetForm();
      setApiError(null);
      setActiveTab(WebsiteModalTypeEnum.ALL_PAGES);
    }
  }, [isModalOpen, resetForm]);

  const handleHelpClick = () => {
    window.open('https://docs.quickchat.ai/ai-agent/knowledge-base/', '_blank');
  };

  const handleTabChange = (value: string) => {
    form.reset();
    setApiError(null);
    setActiveTab(value as WebsiteModalTypeEnum);
  };

  const onSubmit = async (data: UploadUrlForm) => {
    const { useHtmlSelector, htmlSelector, useFilterPhrase, filterPhrase } = data;
    setApiError(null);

    if (useHtmlSelector && (!htmlSelector || htmlSelector.trim() === '')) {
      form.setError('htmlSelector', {
        type: 'manual',
        message: t('modals.upload_url.html_selector.error_required', 'This field is required'),
      });
    } else {
      form.clearErrors('htmlSelector');
    }

    if (
      activeTab === WebsiteModalTypeEnum.SITEMAP &&
      useFilterPhrase &&
      (!filterPhrase || filterPhrase.trim() === '')
    ) {
      form.setError('filterPhrase', {
        type: 'manual',
        message: t('modals.upload_url.filter_phrase.error_required', 'This field is required'),
      });
    } else {
      form.clearErrors('filterPhrase');
    }

    await form.trigger();

    if (form.formState.isValid) {
      try {
        await handleSubmit(data, setApiError);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('modals.upload_url.errors.import_failed', 'Import failed');

        setApiError(errorMessage);
      }
    }
  };

  const isButtonDisabled = isPending || isSubmitting || !isUrlValid;
  const importButtonText =
    isPending || isSubmitting ? t('modals.upload_url.importing') : t('modals.upload_url.import');

  return (
    <div data-testid="upload-url-modal">
      <Dialog open={isModalOpen} onOpenChange={handleClose}>
        <DialogContent className="w-lg">
          <DialogTitle>{t('modals.upload_url.title')}</DialogTitle>

          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="w-full">
              {URL_TABS.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="flex-1"
                  data-testid={tab.testId}
                >
                  {t(tab.labelKey)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <FormProvider {...form}>
            <form key={activeTab} onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="flex flex-col gap-4 py-2">
                <Controller
                  control={form.control}
                  name="url"
                  rules={{ validate: validateFormUrl }}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{UploadUrlModalHelper.getUrlLabel(activeTab, t)}</FormLabel>
                      <DialogDescription>
                        {UploadUrlModalHelper.getDescription(activeTab, t)}
                      </DialogDescription>
                      <FormControl>
                        <div data-testid="upload-url-modal-url-input">
                          <UrlInput
                            type={activeTab}
                            value={field.value}
                            onChange={(value) => {
                              setApiError(null);
                              field.onChange(value);
                            }}
                            onError={handleUrlError}
                            reset={false}
                            error={!!fieldState.error}
                            disabled={isPending || isSubmitting}
                          />
                        </div>
                      </FormControl>
                      <FormMessage error={fieldState.error} />
                      {apiError && !isPending && (
                        <span className="text-sm font-medium text-red-500">{apiError}</span>
                      )}
                    </FormItem>
                  )}
                />

                {/* All Pages maps the site for review; selector/filter only apply at scrape time. */}
                {activeTab !== WebsiteModalTypeEnum.ALL_PAGES && (
                  <Controller
                    control={form.control}
                    name="useHtmlSelector"
                    render={({ fieldState }) => (
                      <FormItem>
                        <AdvancedOptions
                          type={activeTab}
                          onChange={handleAdvancedOptionsChange}
                          reset={false}
                          error={
                            !!(
                              form.formState.errors.htmlSelector ||
                              form.formState.errors.filterPhrase
                            )
                          }
                        />
                        <FormMessage error={fieldState.error} />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <Separator className="bg-accent mt-2" />

              <DialogFooter className="!flex !flex-row !justify-between !items-center mt-2">
                <div
                  className="flex items-center justify-start cursor-pointer"
                  onClick={handleHelpClick}
                >
                  <Icon
                    icon="mingcute:question-line"
                    className="h-5 w-5 text-foreground-subtle group-hover:text-muted-foreground mr-2"
                  />
                  <span className="text-muted-foreground text-sm">
                    {t('modals.upload_url.need_help')}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleClose}
                    type="button"
                    disabled={isPending}
                  >
                    {t('modals.upload_url.cancel')}
                  </Button>
                  <Button
                    data-testid="upload-url-modal-submit-button"
                    variant="dark"
                    type="submit"
                    disabled={isButtonDisabled}
                    className="min-w-32"
                  >
                    {(isPending || isSubmitting) && <Loader2 className="size-4 animate-spin" />}
                    {importButtonText}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Single-type modal (for backward compatibility, e.g. YOUTUBE type used in tests)
const SingleTypeUploadUrlModal: React.FC<{
  type: WebsiteModalTypeEnum;
  externalIsPending: boolean;
}> = ({ type, externalIsPending }) => {
  const { t } = useTranslation(['knowledge_base']);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    form,
    handleSubmit,
    handleClose,
    handleUrlError,
    handleAdvancedOptionsChange,
    validateFormUrl,
    isUrlValid,
    isPending: hookIsPending,
    isSubmitting,
    isModalOpen,
    resetForm,
  } = useUploadUrlForm(type);

  const isPending = externalIsPending || hookIsPending;

  useEffect(() => {
    if (!isModalOpen) {
      resetForm();
      setApiError(null);
    }
  }, [isModalOpen, resetForm]);

  const handleHelpClick = () => {
    window.open('https://docs.quickchat.ai/ai-agent/knowledge-base/', '_blank');
  };

  const onSubmit = async (data: UploadUrlForm) => {
    const { useHtmlSelector, htmlSelector, useFilterPhrase, filterPhrase } = data;
    setApiError(null);

    if (useHtmlSelector && (!htmlSelector || htmlSelector.trim() === '')) {
      form.setError('htmlSelector', {
        type: 'manual',
        message: t('modals.upload_url.html_selector.error_required', 'This field is required'),
      });
    } else {
      form.clearErrors('htmlSelector');
    }

    if (
      (type === WebsiteModalTypeEnum.SITEMAP || type === WebsiteModalTypeEnum.ALL_PAGES) &&
      useFilterPhrase &&
      (!filterPhrase || filterPhrase.trim() === '')
    ) {
      form.setError('filterPhrase', {
        type: 'manual',
        message: t('modals.upload_url.filter_phrase.error_required', 'This field is required'),
      });
    } else {
      form.clearErrors('filterPhrase');
    }

    await form.trigger();

    if (form.formState.isValid) {
      try {
        await handleSubmit(data, setApiError);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('modals.upload_url.errors.import_failed', 'Import failed');

        setApiError(errorMessage);
      }
    }
  };

  const isButtonDisabled = isPending || isSubmitting || !isUrlValid;
  const importButtonText =
    isPending || isSubmitting ? t('modals.upload_url.importing') : t('modals.upload_url.import');

  return (
    <div data-testid="upload-url-modal">
      <Dialog open={isModalOpen} onOpenChange={handleClose}>
        <DialogContent className="w-lg">
          <div className="flex flex-col">
            <DialogTitle>{UploadUrlModalHelper.getTitle(type, t)}</DialogTitle>
            <DialogDescription>{UploadUrlModalHelper.getDescription(type, t)}</DialogDescription>
          </div>

          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="flex flex-col gap-4 py-2">
                <Controller
                  control={form.control}
                  name="url"
                  rules={{ validate: validateFormUrl }}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{UploadUrlModalHelper.getUrlLabel(type, t)}</FormLabel>
                      <FormControl>
                        <div data-testid="upload-url-modal-url-input">
                          <UrlInput
                            type={type}
                            value={field.value}
                            onChange={(value) => {
                              setApiError(null);
                              field.onChange(value);
                            }}
                            onError={handleUrlError}
                            reset={false}
                            error={!!fieldState.error}
                            disabled={isPending || isSubmitting}
                          />
                        </div>
                      </FormControl>
                      <FormMessage error={fieldState.error} />
                      {apiError && !isPending && (
                        <span className="text-sm font-medium text-red-500">{apiError}</span>
                      )}
                    </FormItem>
                  )}
                />

                {type !== WebsiteModalTypeEnum.YOUTUBE && (
                  <Controller
                    control={form.control}
                    name="useHtmlSelector"
                    render={({ fieldState }) => (
                      <FormItem>
                        <AdvancedOptions
                          type={type}
                          onChange={handleAdvancedOptionsChange}
                          reset={false}
                          error={
                            !!(
                              form.formState.errors.htmlSelector ||
                              form.formState.errors.filterPhrase
                            )
                          }
                        />
                        <FormMessage error={fieldState.error} />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <DialogFooter className="!flex !flex-row !justify-between !items-center mt-4">
                <div
                  className="flex items-center justify-start cursor-pointer"
                  onClick={handleHelpClick}
                >
                  <Icon
                    icon="mingcute:question-line"
                    className="h-5 w-5 text-foreground-subtle group-hover:text-muted-foreground mr-2"
                  />
                  <span className="text-muted-foreground text-sm">
                    {t('modals.upload_url.need_help')}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleClose}
                    type="button"
                    disabled={isPending}
                  >
                    {t('modals.upload_url.cancel')}
                  </Button>
                  <Button
                    data-testid="upload-url-modal-submit-button"
                    variant="dark"
                    type="submit"
                    disabled={isButtonDisabled}
                    className="min-w-32"
                  >
                    {(isPending || isSubmitting) && <Loader2 className="size-4 animate-spin" />}
                    {importButtonText}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const UploadUrlModal = ({ type, isPending: externalIsPending = false }: Props) => {
  // When a specific type is provided (e.g., YOUTUBE for backward compat), use single-type modal
  if (type !== undefined) {
    return <SingleTypeUploadUrlModal type={type} externalIsPending={externalIsPending} />;
  }
  // Default: tabbed modal for the 4 URL import types
  return <TabbedUploadUrlModal externalIsPending={externalIsPending} />;
};
